import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Toast } from 'src/components/Toast';
import type { LatestReleaseDismissData } from 'src/constants';
import { UserRoles } from 'src/constants/userConsts';
import history from 'src/history';
import useNavigate from 'src/hooks/useNavigate';
import { useAppSelector } from 'src/hooks/useStore';
import {
  ADDITIONAL_WORKSPACE_PLAN_PARAM,
  isAdditionalWorkspacePlanVisit,
} from 'src/routes/settings/plans/additionalWorkspacePlan';
import { useGetPortalConfigQuery } from 'src/services/api';
import {
  useGetDismissedLatestReleaseQuery,
  useUpdateLatestReleaseDismissalMutation,
} from 'src/services/api/LatestReleaseApi';
import { useGetWhatsNewAnnouncementsQuery } from 'src/services/api/whatsNewApi';
import { selectCurrentUser } from 'src/store/storeUtils';
import { AlertVariant } from 'src/store/ui/types';

import {
  selectWhatsNewAnnouncement,
  shouldShowWhatsNew,
} from './selectWhatsNewAnnouncement';
import { WhatsNewModal } from './WhatsNewModal';

/**
 * `?showLatestRelease=true` forces the modal open (ignoring dismissal and
 * workspace age) and `=hide` forces it closed, which automated test
 * recordings rely on.
 */
const SHOW_OVERRIDE_PARAM = 'showLatestRelease';

const getShowOverride = () =>
  new URLSearchParams(window.location.search).get(SHOW_OVERRIDE_PARAM);

/**
 * A signup re-routed to an existing free workspace lands with the upgrade
 * modal open; showing What's New after it reads like a fresh signup. Closing
 * the upgrade modal strips the param, so the suppression holds until the next
 * page load, and nothing is written so the announcement still shows then.
 */
const useSuppressedByAdditionalWorkspace = () => {
  const [suppressed, setSuppressed] = useState(isAdditionalWorkspacePlanVisit);
  useEffect(() => {
    if (suppressed) return undefined;
    return history.listen((location) => {
      if (
        new URLSearchParams(location.search).get(
          ADDITIONAL_WORKSPACE_PLAN_PARAM,
        ) === '1'
      ) {
        setSuppressed(true);
      }
    });
  }, [suppressed]);
  return suppressed;
};

const openLink = (url: string, navigate: (path: string) => void) => {
  if (url.startsWith('/')) {
    navigate(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const WhatsNew = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isClient = useAppSelector((state) => state.user.isClient);
  const currentUser = useAppSelector(selectCurrentUser);
  const isAdmin =
    currentUser?.fields?.roles?.includes(UserRoles.InternalAdmin) ?? false;

  const showOverride = getShowOverride();
  const isSuppressedByAdditionalWorkspace =
    useSuppressedByAdditionalWorkspace();
  const isForcedOpen = showOverride === 'true';
  const skip =
    isClient ||
    showOverride === 'hide' ||
    (isSuppressedByAdditionalWorkspace && !isForcedOpen);

  const { data: portalConfig } = useGetPortalConfigQuery(undefined, { skip });
  // Refetch on focus so a tab left open picks up announcements published and
  // dismissals made elsewhere before the user can act on a stale modal.
  const { data: announcements = [] } = useGetWhatsNewAnnouncementsQuery(
    undefined,
    { skip, refetchOnFocus: true },
  );
  const { data: dismissal, isLoading: isLoadingDismissal } =
    useGetDismissedLatestReleaseQuery(null, { skip, refetchOnFocus: true });
  const [updateDismissal] = useUpdateLatestReleaseDismissalMutation();
  // Force-open ignores stored dismissal, so track this session's close
  // separately. A normal refresh clears this and re-reads the BE record —
  // dismissed stays hidden; not-yet-dismissed shows again.
  const [isClosed, setIsClosed] = useState(false);

  const announcement = useMemo(
    () =>
      selectWhatsNewAnnouncement({
        announcements,
        isAdmin,
      }),
    [announcements, isAdmin],
  );

  const handleUndoDismiss = useCallback(
    (previous: LatestReleaseDismissData['structFields'] | undefined) => {
      void updateDismissal({
        structFields: {
          latest_release_dismissed: previous?.latest_release_dismissed ?? '',
          latest_release_dismissed_at: previous?.latest_release_dismissed_at,
          restore: true,
        },
      });
      setIsClosed(false);
    },
    [updateDismissal],
  );

  const successDismissSnackbar = useCallback(
    (
      snackbarKey: string | number,
      previous: LatestReleaseDismissData['structFields'] | undefined,
    ) => (
      <div key={snackbarKey}>
        <Toast
          variant={AlertVariant.SUCCESS}
          onClose={() => closeSnackbar(snackbarKey)}
          description={t(($) => $.components.whats_new.release_closed)}
          actionProps={{
            onClick: () => handleUndoDismiss(previous),
            children: t(($) => $.components.whats_new.undo),
            className: 'text-base',
          }}
        />
      </div>
    ),
    [closeSnackbar, handleUndoDismiss, t],
  );

  const handleDismiss = useCallback(async () => {
    if (!announcement) return;
    setIsClosed(true);
    const previous = dismissal?.structFields;
    const result = await updateDismissal({
      structFields: {
        latest_release_dismissed: announcement.id,
        latest_release_dismissed_at: Date.parse(announcement.publishDate),
      },
    });
    // The mutation rolls back its optimistic dismissal on failure, so reopen
    // the modal to match instead of hiding an announcement that isn't saved.
    if (!('data' in result)) {
      setIsClosed(false);
      return;
    }
    enqueueSnackbar(
      t(($) => $.components.whats_new.release_closed),
      {
        content: (snackbarKey: string | number) =>
          successDismissSnackbar(snackbarKey, previous),
      },
    );
  }, [
    announcement,
    dismissal,
    enqueueSnackbar,
    successDismissSnackbar,
    t,
    updateDismissal,
  ]);

  if (skip || isClosed || !announcement || isLoadingDismissal) return null;

  if (
    !shouldShowWhatsNew({
      announcementId: announcement.id,
      publishDate: announcement.publishDate,
      dismissedId: dismissal?.structFields.latest_release_dismissed,
      dismissedAt: dismissal?.structFields.latest_release_dismissed_at,
      workspaceCreatedAt: portalConfig?.createdAt,
      isForcedOpen,
    })
  ) {
    return null;
  }

  return (
    <WhatsNewModal
      announcement={announcement}
      onDismiss={() => {
        void handleDismiss();
      }}
      onOpenLink={(url) => openLink(url, navigate)}
    />
  );
};
